import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { ExternalLink, HardDrive, Loader2, Lock } from "lucide-react";
import { useAccessToken } from "@/auth/useAccessToken";
import {
    driveApi, driveFolderUrl, driveOpenUrl, findDriveLinks, formatDriveBytes, isDriveConfigured,
    type DriveItem
} from "@/api/driveApi";
import { FileTypeIcon } from "./FileTypeIcon";

/**
 * Gmail-style Drive chips under a received message: every
 * drive.athavita.com/open/<id> (or /folder/<id>) link in the body is resolved
 * against Drive with the reader's own token and shown as a card — name, icon,
 * size — or a "No access" card when Drive answers 404. Rendered in the React
 * reading pane, never inside the sandboxed EmailBodyFrame.
 */
export function DriveLinkCards({ html }: { html: string }) {
    const token = useAccessToken();
    const links = useMemo(() => (isDriveConfigured() ? findDriveLinks(html) : []), [html]);

    const results = useQueries({
        queries: links.map((l) => ({
            queryKey: ["drive", "item", l.id, Boolean(token)],
            queryFn: () => driveApi.getItem(l.id, token),
            enabled: Boolean(token),
            staleTime: 60_000,
            // A 404 is an answer ("no access"), not a blip worth retrying.
            retry: false
        }))
    });

    if (links.length === 0) return null;

    return (
        <div className="mt-4" aria-label="Drive files linked in this message">
            <div className="mb-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                <HardDrive className="h-3.5 w-3.5" aria-hidden />
                {links.length === 1 ? "1 Drive file" : `${links.length} Drive files`}
            </div>
            <div className="flex flex-wrap gap-2">
                {links.map((l, i) => {
                    const q = results[i];
                    const item = q?.data as DriveItem | undefined;
                    const href = l.kind === "folder" ? driveFolderUrl(l.id) : driveOpenUrl(l.id);
                    const denied = Boolean(q?.isError);
                    return (
                        <a
                            key={l.id}
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex w-full max-w-[280px] items-center gap-2.5 rounded-md border bg-card py-1.5 pl-1.5 pr-2 text-left shadow-sm transition-colors hover:border-primary/40 hover:bg-accent/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            title={item ? `Open ${item.name} in Drive` : "Open in Drive"}
                        >
                            {item ? (
                                <FileTypeIcon fileName={item.name} isFolder={item.kind === "Folder"} />
                            ) : (
                                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                                    {denied ? <Lock className="h-4 w-4" /> : <Loader2 className="h-4 w-4 animate-spin" />}
                                </span>
                            )}
                            <span className="min-w-0 flex-1">
                                <span className="block truncate text-[13px] font-medium text-foreground">
                                    {item ? item.name : denied ? "No access" : "Loading…"}
                                </span>
                                <span className="block truncate text-[11px] text-muted-foreground">
                                    {item
                                        ? item.kind === "Folder"
                                            ? "Drive folder"
                                            : `${formatDriveBytes(item.sizeBytes)}${item.ownerEmail ? ` · ${item.ownerEmail}` : ""}`
                                        : denied
                                            ? "Ask the sender to share it with you"
                                            : "Athavita Drive"}
                                </span>
                            </span>
                            <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                        </a>
                    );
                })}
            </div>
        </div>
    );
}
